import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiX, FiRefreshCw, FiSearch, FiFilter } from 'react-icons/fi';
import { MdQrCode } from 'react-icons/md';
import { db } from '../../services/supabase';
import toast from 'react-hot-toast';
import CheckCircleIcon from '../common/icons/CheckCircleIcon';
import WarningIcon from '../common/icons/WarningIcon';
import MoneyIcon from '../common/icons/MoneyIcon';
import TimeIcon from '../common/icons/TimeIcon';
import InboxIcon from '../common/icons/InboxIcon';

const PaymentManager = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const queryClient = useQueryClient();

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 0 }),
    []
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  const {
    data: orders,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['orders', searchTerm, statusFilter],
    queryFn: async () => {
      const result = await db.orders.getFiltered({
        search: searchTerm,
        status: statusFilter,
      });
      return result ?? [];
    },
    refetchInterval: 15000,
  });

  const approveMutation = useMutation({
    mutationFn: (orderId) => db.orders.approve(orderId),
    onSuccess: (_, orderId) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      const order = orders?.find((o) => o.id === orderId);
      toast.success(`Platba schválená pre ${order?.first_name} ${order?.last_name}`, {
        icon: <CheckCircleIcon className="text-success-400" aria-hidden="true" />,
      });
    },
    onError: (mutationError) => {
      toast.error(`Chyba pri schvaľovaní: ${mutationError.message}`, {
        icon: <WarningIcon className="text-error-400" aria-hidden="true" />,
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (orderId) => db.orders.reject(orderId),
    onSuccess: (_, orderId) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      const order = orders?.find((o) => o.id === orderId);
      toast.success(`Objednávka zrušená: ${order?.first_name} ${order?.last_name}`, {
        icon: <WarningIcon className="text-warning-400" aria-hidden="true" />,
      });
    },
    onError: (mutationError) => {
      toast.error(`Chyba pri rušení: ${mutationError.message}`, {
        icon: <WarningIcon className="text-error-400" aria-hidden="true" />,
      });
    },
  });

  const normalizedOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    return orders.map((order) => {
      // Zaisťujeme, že tickets je array reťazcov (ticket_number), nie objektov
      let tickets = [];
      if (Array.isArray(order.tickets)) {
        // Ak je to array, skontrolujeme, či obsahuje reťazce alebo objekty
        tickets = order.tickets.map(t => {
          if (typeof t === 'string') return t;
          if (typeof t === 'object' && t?.ticket_number) return t.ticket_number;
          return String(t);
        });
      }
      return {
        ...order,
        tickets,
        total_amount: Number(order.total_amount ?? 0),
        ticket_count: Number(order.ticket_count ?? tickets.length ?? 0),
      };
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!searchTerm) return normalizedOrders;
    const term = searchTerm.toLowerCase();
    return normalizedOrders.filter((order) => {
      const parts = [
        order.first_name,
        order.last_name,
        order.email,
        order.qr_code,
        order.phone,
      ].filter(Boolean);
      return parts.some((value) => value.toLowerCase().includes(term));
    });
  }, [normalizedOrders, searchTerm]);

  const groupedOrders = useMemo(() => {
    const pending = filteredOrders.filter((order) => order.status === 'pending');
    const paid = filteredOrders.filter((order) => order.status === 'paid');
    const cancelled = filteredOrders.filter((order) => order.status === 'cancelled');
    const totalRevenue = paid.reduce((sum, order) => sum + order.total_amount, 0);
    const pendingAmount = pending.reduce((sum, order) => sum + order.total_amount, 0);

    const promoOrders = filteredOrders.filter((order) => order.status !== 'cancelled');
    const freeTicketByOrder = promoOrders.map((order) => ({
      order,
      freeTickets: Math.floor(Number(order.ticket_count ?? 0) / 6),
    }));
    const promoOrdersWithFree = freeTicketByOrder.filter((entry) => entry.freeTickets > 0);
    const freeUsersCount = new Set(
      promoOrdersWithFree.map((entry) => String(entry.order.email ?? '').trim().toLowerCase()).filter(Boolean)
    ).size;
    const freeTicketsTotal = promoOrdersWithFree.reduce((sum, entry) => sum + entry.freeTickets, 0);

    return {
      pending,
      paid,
      cancelled,
      totalRevenue,
      pendingAmount,
      freeUsersCount,
      freeTicketsTotal,
    };
  }, [filteredOrders]);

  const handleApprove = (orderId) => {
    if (window.confirm('Potvrdiť schválenie platby?')) {
      approveMutation.mutate(orderId);
    }
  };

  const handleReject = (orderId) => {
    if (window.confirm('Potvrdiť zrušenie objednávky? Táto akcia sa nedá vrátiť.')) {
      rejectMutation.mutate(orderId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center text-slate-200">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-400 mx-auto mb-4" />
          <p className="text-lg text-slate-300">Načítavam objednávky...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <div className="text-error-400 text-6xl mb-4 flex justify-center">
          <WarningIcon className="text-5xl" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-primary-100 mb-4">
          Chyba pri načítaní objednávok
        </h3>
        <button onClick={() => refetch()} className="btn btn-primary">
          <FiRefreshCw className="w-4 h-4 mr-2" />
          Skúsiť znovu
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hlavička */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-primary-100 flex items-center gap-3">
            <MoneyIcon className="text-primary-300" aria-hidden="true" />
            <span>Správa platieb</span>
          </h1>
          <p className="text-slate-400 mt-1">Schvaľovanie a správa objednávok</p>
        </div>
        <button onClick={() => refetch()} className="btn btn-secondary flex items-center gap-2">
          <FiRefreshCw className="w-4 h-4" />
          Obnoviť
        </button>
      </div>

      {/* Štatistiky */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          {
            label: 'Čakajúce platby',
            value: numberFormatter.format(groupedOrders.pending.length),
            description: 'Vyžadujú manuálne schválenie',
          },
          {
            label: 'Zaplatené',
            value: numberFormatter.format(groupedOrders.paid.length),
            description: 'Platby potvrdené administrátorom',
          },
          {
            label: 'Zrušené',
            value: numberFormatter.format(groupedOrders.cancelled.length),
            description: 'Stiahnuté alebo zamietnuté objednávky',
          },
          {
            label: 'Čaká na úhradu',
            value: currencyFormatter.format(groupedOrders.pendingAmount),
            description: 'Suma čakajúcich platieb',
          },
          {
            label: 'Celkový príjem',
            value: currencyFormatter.format(groupedOrders.totalRevenue),
            description: 'Sčítané zaplatené objednávky',
          },
          {
            label: 'Využili zdarma',
            value: numberFormatter.format(groupedOrders.freeUsersCount),
            description: `${numberFormatter.format(groupedOrders.freeTicketsTotal)} lístkov zdarma spolu`,
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60"
          >
            <p className="text-sm text-slate-400">{stat.label}</p>
            <p className="text-2xl font-semibold text-slate-100 mt-2">{stat.value}</p>
            <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
          </div>
        ))}
      </div>

      {/* Filtre */}
      <div className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
            <input
              type="text"
              placeholder="Hľadať podľa mena, emailu alebo QR kódu..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            />
          </div>
          <div className="relative">
            <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="pl-10 pr-8 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 appearance-none"
            >
              <option value="all">Všetky stavy</option>
              <option value="pending">Čakajúce</option>
              <option value="paid">Zaplatené</option>
              <option value="cancelled">Zrušené</option>
            </select>
          </div>
        </div>
      </div>

      {/* Čakajúce platby */}
      {groupedOrders.pending.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card backdrop-blur-sm border border-warning-500/30 bg-warning-500/5"
        >
          <div className="pb-6 border-b border-slate-800/60 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-primary-100 flex items-center gap-2">
              <TimeIcon className="text-warning-200" aria-hidden="true" />
              <span>Čakajúce platby ({groupedOrders.pending.length})</span>
            </h2>
          </div>
          <div className="divide-y divide-slate-800/40">
            <AnimatePresence>
              {groupedOrders.pending.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="py-6 flex flex-col gap-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6 text-slate-200">
                    <div className="flex-1 space-y-2">
                      <h3 className="text-lg font-semibold text-slate-100">
                        {order.first_name} {order.last_name}
                      </h3>
                      <p className="text-sm text-slate-400">{order.email}</p>
                      {order.phone && <p className="text-sm text-slate-500">{order.phone}</p>}
                      <p className="text-xs text-slate-500 font-mono break-all">
                        ID objednávky: {order.id}
                      </p>
                    </div>

                    <div className="flex-1">
                      <p className="text-sm text-slate-400 mb-2">
                        Lístky ({numberFormatter.format(order.ticket_count)}):
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {order.tickets.map((ticket) => (
                          <span
                            key={`${order.id}-${ticket}`}
                            className="px-2 py-1 rounded bg-primary-500/10 text-primary-200 text-xs font-medium border border-primary-500/30"
                          >
                            {ticket}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="bg-slate-800/80 rounded-lg p-3 mb-2">
                        <MdQrCode className="w-8 h-8 text-slate-400 mx-auto" />
                      </div>
                      <div className="font-mono text-xs text-slate-500 max-w-32 break-all">
                        {order.qr_code ?? '—'}
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary-100">
                        {currencyFormatter.format(order.total_amount)}
                      </div>
                      <div className="text-sm text-slate-400">k úhrade</div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(order.id)}
                        disabled={approveMutation.isPending}
                        className="btn btn-success flex items-center gap-2 disabled:opacity-50"
                      >
                        <FiCheck className="w-4 h-4" />
                        Schváliť
                      </button>
                      <button
                        onClick={() => handleReject(order.id)}
                        disabled={rejectMutation.isPending}
                        className="btn btn-danger flex items-center gap-2 disabled:opacity-50"
                      >
                        <FiX className="w-4 h-4" />
                        Zrušiť
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* Zaplatené objednávky */}
      {groupedOrders.paid.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card backdrop-blur-sm border border-success-500/30 bg-success-500/5"
        >
          <div className="pb-4 border-b border-slate-800/60 flex items-center gap-2">
            <CheckCircleIcon className="text-success-200" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-primary-100">
              Zaplatené objednávky ({groupedOrders.paid.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {groupedOrders.paid.map((order) => (
              <div key={order.id} className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/50 hover:border-success-500/50 transition-colors">
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-slate-100 text-lg">
                      {order.first_name} {order.last_name}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{order.email}</p>
                    <p className="text-xs text-slate-500 font-mono break-all mt-1">{order.id}</p>
                  </div>
                  <div className="pt-2 border-t border-slate-700/50">
                    <div className="text-xl font-bold text-success-200">
                      {currencyFormatter.format(order.total_amount)}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      {numberFormatter.format(order.ticket_count)} lístkov
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {order.updated_at ? new Date(order.updated_at).toLocaleString('sk') : '—'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Žiadne objednávky */}
      {filteredOrders.length === 0 && (
        <div className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60 text-center py-12">
          <div className="text-6xl mb-4 flex justify-center text-slate-600">
            <InboxIcon aria-hidden="true" />
          </div>
          <h3 className="text-xl font-semibold text-primary-100 mb-2">Žiadne objednávky</h3>
          <p className="text-slate-400">
            {searchTerm || statusFilter !== 'all'
              ? 'Žiadne objednávky nevyhovujú aktuálnym filtrom.'
              : 'Zatiaľ neboli vytvorené žiadne objednávky.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentManager;
